import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BookMarked,
  BookOpen,
  Plus,
  Search,
  Trash2,
  Edit2,
  Check,
  X,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ConfirmModal } from "../../components/base/BaseModal";
import { showToast } from "../../utils/toastNotification";
import { DEFAULT_LEGAL_DOC_TYPES } from "../../components/documents/LegalDocumentFormModal";
import { DEFAULT_HISTORY_SUB_CATEGORIES } from "../../components/documents/DocumentFormModal";
import {
  legalDocumentCategoriesApi,
  historyDocumentCategoriesApi,
} from "../../apis/documentCategories";

const ITEMS_PER_PAGE = 8;

const CATEGORY_NAME_REGEX = /^[\p{L}\p{M}\p{N}\s\-]+$/u;

const normalizeCategory = (item) => ({
  ...item,
  description: item.description || "",
  createdAt: item.createdAt
    ? new Date(item.createdAt).toLocaleString("vi-VN")
    : "",
});

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Không thể thực hiện thao tác";

export default function DocumentCategoriesManagement() {
  const [activeTab, setActiveTab] = useState("legal"); // "legal" | "history"
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);

  const initializeItems = (defaultList) => defaultList.map((item) => ({
      ...item,
      description: item.description && item.description !== "Không có mô tả" ? item.description : "",
      createdAt: "",
      status: "ACTIVE",
    }));

  // Storage states
  const [legalTypes, setLegalTypes] = useState(() =>
    initializeItems(DEFAULT_LEGAL_DOC_TYPES)
  );

  const [historyCategories, setHistoryCategories] = useState(() =>
    initializeItems(DEFAULT_HISTORY_SUB_CATEGORIES)
  );
  const [isLoading, setIsLoading] = useState(false);

  const loadCategories = useCallback(async (tab = "legal") => {
    setIsLoading(true);
    try {
      const api = tab === "legal" ? legalDocumentCategoriesApi : historyDocumentCategoriesApi;
      const response = await api.list();
      const items = (response.data?.data || []).map(normalizeCategory);
      if (tab === "legal") setLegalTypes(items);
      else setHistoryCategories(items);
    } catch (error) {
      showToast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories(activeTab);
  }, [activeTab, loadCategories]);

  // Inline Quick Add State
  const [isAddingInline, setIsAddingInline] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeDescription, setNewTypeDescription] = useState("");
  const [newTypeError, setNewTypeError] = useState("");

  // Inline Row Edit State
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [editingError, setEditingError] = useState("");

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  // Current active list & helper variables
  const currentList = activeTab === "legal" ? legalTypes : historyCategories;
  const currentLabel = activeTab === "legal" ? "Loại văn bản" : "Phân nhóm";

  // Filtered list
  const filteredList = currentList.filter(
    (item) =>
      (item.name || "").toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
      (item.description || "").toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  // Paginated slices
  const totalPages = Math.max(1, Math.ceil(filteredList.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginatedList = filteredList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Scroll detection for sticky action column shadow (matching desktop BaseTable)
  const [isAtEnd, setIsAtEnd] = useState(false);
  const scrollContainerRef = useRef(null);

  const checkScrollEnd = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atRightEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 3;
    const noScroll = el.scrollWidth <= el.clientWidth;
    setIsAtEnd(atRightEnd || noScroll);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const timer = setTimeout(checkScrollEnd, 50);
    el.addEventListener("scroll", checkScrollEnd, { passive: true });
    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(checkScrollEnd);
      resizeObserver.observe(el);
    } else {
      window.addEventListener("resize", checkScrollEnd);
    }

    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", checkScrollEnd);
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener("resize", checkScrollEnd);
    };
  }, [checkScrollEnd, activeTab, currentList, currentPage]);

  const stickyShadowClass = `before:absolute before:top-0 before:bottom-0 before:left-0 before:w-6 before:-translate-x-full before:bg-gradient-to-l before:from-black/15 before:to-transparent before:pointer-events-none before:transition-opacity before:duration-300 before:ease-in-out ${
    isAtEnd ? "before:opacity-0 shadow-none" : "before:opacity-100 shadow-[-6px_0_12px_-2px_rgba(0,0,0,0.12)]"
  }`;

  // Switch tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
    setSearchInput("");
    setPage(1);
    setIsAddingInline(false);
    setNewTypeName("");
    setNewTypeDescription("");
    setNewTypeError("");
    setEditingId(null);
    setEditingName("");
    setEditingDescription("");
    setEditingError("");
  };

  // Format current date time helper
  const getCurrentFormattedTime = () => {
    const now = new Date();
    const d = String(now.getDate()).padStart(2, "0");
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const y = now.getFullYear();
    const hr = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    return `${d}/${m}/${y} ${hr}:${min}`;
  };

  // Handle Search Execution
  const handleExecuteSearch = (e) => {
    if (e) e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1);
  };

  // Submit Inline Add
  const handleSubmitInlineAdd = async (e) => {
    if (e) e.preventDefault();
    const trimmedName = newTypeName.trim().normalize("NFC");
    const trimmedDesc = newTypeDescription.trim();

    if (!trimmedName || trimmedName.length < 1) {
      setNewTypeError(`Vui lòng nhập tên ${currentLabel.toLowerCase()}`);
      return;
    }
    if (trimmedName.length > 50) {
      setNewTypeError(`Tên ${currentLabel.toLowerCase()} không được vượt quá 50 ký tự`);
      return;
    }
    if (!CATEGORY_NAME_REGEX.test(trimmedName)) {
      setNewTypeError(`Không được nhập ký tự lạ (chỉ cho phép chữ cái, chữ số và dấu -)`);
      return;
    }
    if (trimmedDesc.length > 255) {
      setNewTypeError("Mô tả không được vượt quá 255 ký tự");
      return;
    }

    const isDuplicate = currentList.some(
      (item) => item.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setNewTypeError(`Tên ${currentLabel.toLowerCase()} này đã tồn tại`);
      return;
    }

    try {
      const api = activeTab === "legal" ? legalDocumentCategoriesApi : historyDocumentCategoriesApi;
      await api.create({ name: trimmedName, description: trimmedDesc || "Không có mô tả" });
      await loadCategories(activeTab);
    } catch (error) {
      setNewTypeError(getErrorMessage(error));
      return;
    }

    showToast.success(`Đã thêm ${currentLabel.toLowerCase()} mới: "${trimmedName}"`);
    setNewTypeName("");
    setNewTypeDescription("");
    setNewTypeError("");
    setIsAddingInline(false);
    setPage(1);
  };

  // Start Row Edit
  const handleStartEdit = (item) => {
    setIsAddingInline(false);
    setEditingId(item.id);
    setEditingName(item.name);
    setEditingDescription(item.description || "");
    setEditingError("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingDescription("");
    setEditingError("");
  };

  // Save Row Edit
  const handleSaveEdit = async (id) => {
    const trimmedName = editingName.trim().normalize("NFC");
    const trimmedDesc = editingDescription.trim();

    if (!trimmedName || trimmedName.length < 1) {
      setEditingError(`Vui lòng nhập tên ${currentLabel.toLowerCase()}`);
      return;
    }
    if (trimmedName.length > 50) {
      setEditingError(`Tên ${currentLabel.toLowerCase()} không được vượt quá 50 ký tự`);
      return;
    }
    if (!CATEGORY_NAME_REGEX.test(trimmedName)) {
      setEditingError(`Không được nhập ký tự lạ (chỉ cho phép chữ cái, chữ số và dấu -)`);
      return;
    }
    if (trimmedDesc.length > 255) {
      setEditingError("Mô tả không được vượt quá 255 ký tự");
      return;
    }

    const isDuplicate = currentList.some(
      (item) => item.name.trim().toLowerCase() === trimmedName.toLowerCase() && item.id !== id
    );
    if (isDuplicate) {
      setEditingError(`Tên ${currentLabel.toLowerCase()} này đã tồn tại`);
      return;
    }

    try {
      const api = activeTab === "legal" ? legalDocumentCategoriesApi : historyDocumentCategoriesApi;
      await api.update(id, { name: trimmedName, description: trimmedDesc || "Không có mô tả" });
      await loadCategories(activeTab);
    } catch (error) {
      setEditingError(getErrorMessage(error));
      return;
    }

    showToast.success(`Đã cập nhật ${currentLabel.toLowerCase()}: "${trimmedName}"`);
    setEditingId(null);
    setEditingName("");
    setEditingDescription("");
    setEditingError("");
  };

  // Delete Handlers
  const handleOpenDelete = (item) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    try {
      const api = activeTab === "legal" ? legalDocumentCategoriesApi : historyDocumentCategoriesApi;
      await api.remove(deletingItem.id);
      await loadCategories(activeTab);
    } catch (error) {
      showToast.error(getErrorMessage(error));
      return;
    }
    showToast.success(`Đã xóa ${currentLabel.toLowerCase()}: "${deletingItem.name}"`);

    if (editingId === deletingItem.id) {
      handleCancelEdit();
    }

    setIsDeleteModalOpen(false);
    setDeletingItem(null);
  };

  return (
    <div className="space-y-5">
      {/* Top Header matching reference screenshot */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý danh mục loại văn bản</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Quản lý các danh mục văn bản trong hệ thống
          </p>
        </div>

        {/* Top Right Action Button */}
        <button
          type="button"
          onClick={() => {
            setIsAddingInline(true);
            setNewTypeName("");
            setNewTypeDescription("");
            setNewTypeError("");
            setEditingId(null);
          }}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm shadow-xs transition-colors cursor-pointer whitespace-nowrap"
        >
          <Plus className="w-4 h-4" />
          <span>Thêm danh mục mới</span>
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200 gap-2">
        <button
          type="button"
          onClick={() => handleTabChange("legal")}
          className={`flex items-center gap-2 pb-3 px-4 font-semibold text-sm border-b-2 transition-colors cursor-pointer ${
            activeTab === "legal"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <BookMarked className="w-4 h-4" />
          <span>Quy phạm pháp luật</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange("history")}
          className={`flex items-center gap-2 pb-3 px-4 font-semibold text-sm border-b-2 transition-colors cursor-pointer ${
            activeTab === "history"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <BookOpen className="w-4 h-4" />
          <span>Văn hóa - Lịch sử</span>
        </button>
      </div>

      {/* Search & Filter Bar Card matching screenshot */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs">
        <form
          onSubmit={handleExecuteSearch}
          className="flex flex-col sm:flex-row items-center gap-3"
        >
          {/* Search Input Box */}
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Tìm kiếm theo tên danh mục..."
              className="w-full pl-10 pr-9 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-gray-400"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setSearchQuery("");
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                title="Xóa tìm kiếm"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Action Button: Tìm kiếm */}
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              type="submit"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-xs transition-colors cursor-pointer whitespace-nowrap"
            >
              <Search className="w-4 h-4" />
              <span>Tìm kiếm</span>
            </button>
          </div>
        </form>
      </div>

      {/* Main Table Container Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
        {/* Card Header matching Screenshot */}
        <div className="p-4 sm:px-6 flex items-center justify-between border-b border-gray-100 bg-white">
          <h2 className="text-base font-bold text-gray-900">
            Danh sách danh mục ({filteredList.length})
          </h2>
          {isLoading && <span className="text-xs text-gray-500">Đang tải...</span>}
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto" ref={scrollContainerRef}>
          <table className="w-full min-w-[680px] divide-y divide-gray-100">
            <thead className="bg-white">
              <tr className="border-b border-gray-200">
                <th scope="col" className="w-14 min-w-[50px] px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  STT
                </th>
                <th scope="col" className="min-w-[180px] px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  TÊN DANH MỤC
                </th>
                <th scope="col" className="min-w-[180px] px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  MÔ TẢ
                </th>
                <th scope="col" className="w-36 min-w-[130px] px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  NGÀY TẠO
                </th>
                <th scope="col" className={`w-24 min-w-[90px] px-3 sm:px-6 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap sticky right-0 bg-white z-20 border-l border-gray-200 ${stickyShadowClass}`}>
                  THAO TÁC
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {/* ===== Inline Quick Add Row ===== */}
              {isAddingInline && (
                <tr className="bg-blue-50/60 border-y-2 border-blue-500 animate-fadeIn">
                  <td className="px-3 sm:px-6 py-3 text-left">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold shadow-xs">
                      +
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-3">
                    <input
                      type="text"
                      autoFocus
                      value={newTypeName}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewTypeName(val);
                        if (val && !CATEGORY_NAME_REGEX.test(val)) {
                          setNewTypeError(`Không được nhập ký tự lạ (chỉ cho phép chữ cái, chữ số và dấu -)`);
                        } else if (newTypeError) {
                          setNewTypeError("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (newTypeName.trim() && CATEGORY_NAME_REGEX.test(newTypeName.trim())) {
                            handleSubmitInlineAdd();
                          }
                        } else if (e.key === "Escape") {
                          setIsAddingInline(false);
                          setNewTypeName("");
                          setNewTypeDescription("");
                          setNewTypeError("");
                        }
                      }}
                      placeholder={`Nhập tên ${currentLabel.toLowerCase()} mới...`}
                      maxLength={50}
                      className={`w-full px-3 py-1.5 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        newTypeError ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                      }`}
                    />
                    <div className="flex items-center justify-between mt-1 text-xs">
                      <div>
                        {newTypeError && (
                          <p className="text-red-600 font-medium">{newTypeError}</p>
                        )}
                      </div>
                      <span className={`ml-auto font-medium ${newTypeName.length >= 50 ? "text-red-500" : "text-gray-400"}`}>
                        {newTypeName.length}/50
                      </span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3">
                    <input
                      type="text"
                      value={newTypeDescription}
                      onChange={(e) => setNewTypeDescription(e.target.value)}
                      placeholder="Nhập mô tả danh mục (tùy chọn)..."
                      maxLength={255}
                      className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-3 sm:px-6 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {getCurrentFormattedTime()}
                  </td>
                  <td className={`px-3 sm:px-6 py-3 text-center whitespace-nowrap sticky right-0 bg-blue-50 z-10 border-l border-blue-200 ${stickyShadowClass}`}>
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={handleSubmitInlineAdd}
                        disabled={!newTypeName.trim() || !CATEGORY_NAME_REGEX.test(newTypeName.trim())}
                        className={`p-1.5 rounded-md shadow-xs transition-colors ${
                          !newTypeName.trim() || !CATEGORY_NAME_REGEX.test(newTypeName.trim())
                            ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                        }`}
                        title="Lưu thêm mới"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingInline(false);
                          setNewTypeName("");
                          setNewTypeDescription("");
                          setNewTypeError("");
                        }}
                        className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors cursor-pointer"
                        title="Hủy"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* ===== Empty State ===== */}
              {filteredList.length === 0 && !isAddingInline ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <HelpCircle className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-600">
                      Không tìm thấy {currentLabel.toLowerCase()} nào
                    </p>
                    {searchQuery ? (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchInput("");
                          setSearchQuery("");
                          setPage(1);
                        }}
                        className="mt-2 text-xs font-medium text-blue-600 hover:underline cursor-pointer"
                      >
                        Xóa bộ lọc tìm kiếm
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingInline(true);
                          setNewTypeName("");
                          setNewTypeDescription("");
                          setNewTypeError("");
                        }}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Thêm {currentLabel.toLowerCase()} đầu tiên</span>
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                paginatedList.map((item, index) => {
                  const isEditing = editingId === item.id;
                  const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                  return (
                    <tr
                      key={item.id}
                      className={`transition-colors ${
                        isEditing ? "bg-amber-50/70 border-l-4 border-amber-500" : "hover:bg-gray-50/70"
                      }`}
                    >
                      {/* STT Column like screenshot (#1, #2...) */}
                      <td className="px-3 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                        #{globalIndex}
                      </td>

                      {/* TÊN DANH MỤC Column */}
                      <td className="px-3 sm:px-6 py-3.5">
                        {isEditing ? (
                          <div className="space-y-1">
                            <input
                              type="text"
                              autoFocus
                              value={editingName}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditingName(val);
                                if (val && !CATEGORY_NAME_REGEX.test(val)) {
                                  setEditingError(`Không được nhập ký tự lạ (chỉ cho phép chữ cái, chữ số và dấu -)`);
                                } else if (editingError) {
                                  setEditingError("");
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  if (editingName.trim() && CATEGORY_NAME_REGEX.test(editingName.trim())) {
                                    handleSaveEdit(item.id);
                                  }
                                } else if (e.key === "Escape") {
                                  handleCancelEdit();
                                }
                              }}
                              maxLength={50}
                              className={`w-full px-3 py-1 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                editingError ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                              }`}
                            />
                            <div className="flex items-center justify-between text-xs">
                              <div>
                                {editingError && (
                                  <p className="text-red-600 font-medium">{editingError}</p>
                                )}
                              </div>
                              <span className={`ml-auto font-medium ${editingName.length >= 50 ? "text-red-500" : "text-gray-400"}`}>
                                {editingName.length}/50
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-gray-900 block break-words">
                            {item.name}
                          </span>
                        )}
                      </td>

                      {/* MÔ TẢ Column */}
                      <td className="px-3 sm:px-6 py-3.5">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingDescription}
                            onChange={(e) => setEditingDescription(e.target.value)}
                            placeholder="Mô tả danh mục..."
                            maxLength={255}
                            className="w-full px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-gray-600 block break-words">
                            {item.description || "—"}
                          </span>
                        )}
                      </td>

                      {/* NGÀY TẠO Column */}
                      <td className="px-3 sm:px-6 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                        {item.createdAt || "—"}
                      </td>

                      {/* THAO TÁC Column (Purple Edit + Red Delete) */}
                      <td className={`px-3 sm:px-6 py-3.5 text-center whitespace-nowrap sticky right-0 z-10 border-l ${stickyShadowClass} ${
                        isEditing ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"
                      }`}>
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleSaveEdit(item.id)}
                              disabled={!editingName.trim() || !CATEGORY_NAME_REGEX.test(editingName.trim())}
                              className={`p-1.5 rounded-md shadow-xs transition-colors ${
                                !editingName.trim() || !CATEGORY_NAME_REGEX.test(editingName.trim())
                                  ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                                  : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                              }`}
                              title="Lưu thay đổi"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors cursor-pointer"
                              title="Hủy"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2">
                            {/* Purple Edit Icon matching screenshot */}
                            <button
                              type="button"
                              onClick={() => handleStartEdit(item)}
                              className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors cursor-pointer"
                              title="Chỉnh sửa danh mục"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>

                            {/* Delete Action */}
                            <button
                              type="button"
                              onClick={() => handleOpenDelete(item)}
                              className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                              title="Xóa danh mục"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Card Footer with Standard Pagination */}
        <div className="px-6 py-3.5 border-t border-gray-100 bg-white flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <span>
            {filteredList.length > 0 ? (
              <>
                Hiển thị <strong>{(currentPage - 1) * ITEMS_PER_PAGE + 1}</strong> - <strong>{Math.min(currentPage * ITEMS_PER_PAGE, filteredList.length)}</strong> trong tổng số <strong>{filteredList.length}</strong> danh mục
              </>
            ) : (
              "Không có kết quả nào"
            )}
          </span>

          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Trang trước"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setPage(pageNum)}
                  className={`w-7 h-7 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                    pageNum === currentPage
                      ? "bg-blue-600 text-white shadow-xs"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Trang sau"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Xác nhận xóa ${currentLabel.toLowerCase()}`}
        message={
          deletingItem
            ? `Bạn có chắc chắn muốn xóa danh mục "${deletingItem.name}"? Danh mục này sẽ không còn xuất hiện trong các form tạo tài liệu.`
            : ""
        }
        confirmText="Xác nhận xóa"
        cancelText="Hủy"
        type="danger"
      />
    </div>
  );
}


